/**
 * Activation — the moment a holder wakes their agent.
 *
 * The holder signs an EIP-712 message. That is all. The NFT never moves, never
 * gets locked, and never leaves the wallet. The platform submits the signature
 * on-chain and pays the gas, so activation is free for the holder.
 *
 * The contract enforces: the signer must be the token's owner at execution
 * time, and activation is one-shot per holding.
 */

import type { Address, WalletClient } from "viem";

export interface ActivationTypedData {
  domain: {
    name: string;
    version: string;
    chainId: number;
    verifyingContract: Address;
  };
  types: {
    Activation: readonly [
      { name: "tokenId"; type: "uint256" },
      { name: "holder"; type: "address" },
      { name: "deadline"; type: "uint256" },
    ];
  };
  primaryType: "Activation";
  message: {
    tokenId: bigint;
    holder: Address;
    deadline: bigint;
  };
}

/** Build the EIP-712 payload the contract's activateWithSig() expects. */
export function buildActivationTypedData(params: {
  tokenId: bigint;
  holder: Address;
  chainId: number;
  contractAddress: Address;
  /** Seconds until the signature expires (default 30 minutes). */
  ttlSeconds?: number;
}): ActivationTypedData {
  const ttl = BigInt(params.ttlSeconds ?? 1800);
  const deadline = BigInt(Math.floor(Date.now() / 1000)) + ttl;

  return {
    domain: {
      name: "HoodStreet",
      version: "1",
      chainId: params.chainId,
      verifyingContract: params.contractAddress,
    },
    types: {
      Activation: [
        { name: "tokenId", type: "uint256" },
        { name: "holder", type: "address" },
        { name: "deadline", type: "uint256" },
      ],
    },
    primaryType: "Activation",
    message: {
      tokenId: params.tokenId,
      holder: params.holder,
      deadline,
    },
  };
}

/** Ask the wallet to sign. Returns the 65-byte signature. */
export async function signActivation(
  wallet: WalletClient,
  account: Address,
  typedData: ActivationTypedData
): Promise<`0x${string}`> {
  return wallet.signTypedData({
    account,
    domain: typedData.domain,
    types: typedData.types,
    primaryType: typedData.primaryType,
    message: typedData.message,
  });
}

/** Submit a signed activation to the backend, which relays it on-chain. */
export async function submitActivation(params: {
  apiBase: string;
  tokenId: bigint;
  holder: Address;
  deadline: bigint;
  signature: `0x${string}`;
}): Promise<RelayOutcome> {
  const res = await fetch(`${params.apiBase}/api/activate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      tokenId: params.tokenId.toString(),
      holder: params.holder,
      deadline: params.deadline.toString(),
      signature: params.signature,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`activation failed (${res.status}): ${text}`);
  }

  return (await res.json()) as RelayOutcome;
}

/* ------------------------------------------------------------------
   Model choice ("brain") — same gasless pattern, plus a nonce so every
   signature is one-shot.
------------------------------------------------------------------ */

export interface SetModelTypedData {
  domain: {
    name: string;
    version: string;
    chainId: number;
    verifyingContract: Address;
  };
  types: {
    SetModel: readonly [
      { name: "tokenId"; type: "uint256" },
      { name: "holder"; type: "address" },
      { name: "modelId"; type: "uint8" },
      { name: "nonce"; type: "uint256" },
      { name: "deadline"; type: "uint256" },
    ];
  };
  primaryType: "SetModel";
  message: {
    tokenId: bigint;
    holder: Address;
    modelId: number;
    nonce: bigint;
    deadline: bigint;
  };
}

/** Build the EIP-712 payload the contract's setModelWithSig() expects. */
export function buildSetModelTypedData(params: {
  tokenId: bigint;
  holder: Address;
  modelId: number;
  nonce: bigint;
  chainId: number;
  contractAddress: Address;
  /** Seconds until the signature expires (default 30 minutes). */
  ttlSeconds?: number;
}): SetModelTypedData {
  const ttl = BigInt(params.ttlSeconds ?? 1800);
  const deadline = BigInt(Math.floor(Date.now() / 1000)) + ttl;

  return {
    domain: {
      name: "HoodStreet",
      version: "1",
      chainId: params.chainId,
      verifyingContract: params.contractAddress,
    },
    types: {
      SetModel: [
        { name: "tokenId", type: "uint256" },
        { name: "holder", type: "address" },
        { name: "modelId", type: "uint8" },
        { name: "nonce", type: "uint256" },
        { name: "deadline", type: "uint256" },
      ],
    },
    primaryType: "SetModel",
    message: {
      tokenId: params.tokenId,
      holder: params.holder,
      modelId: params.modelId,
      nonce: params.nonce,
      deadline,
    },
  };
}

/** Submit a signed brain change to the backend, which relays it on-chain. */
export async function submitSetModel(params: {
  apiBase: string;
  tokenId: bigint;
  holder: Address;
  modelId: number;
  nonce: bigint;
  deadline: bigint;
  signature: `0x${string}`;
}): Promise<RelayOutcome> {
  const res = await fetch(`${params.apiBase}/api/set-model`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      tokenId: params.tokenId.toString(),
      holder: params.holder,
      modelId: String(params.modelId),
      nonce: params.nonce.toString(),
      deadline: params.deadline.toString(),
      signature: params.signature,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`set brain failed (${res.status}): ${text}`);
  }

  return (await res.json()) as RelayOutcome;
}

/** What the relay reports back: the signed action, plus any payout it pushed. */
export interface RelayOutcome {
  /** The activation / model transaction. */
  txHash: string;
  /** `release(tokenId)` — the $1.00 stake share — when it landed. */
  releaseTxHash?: string | null;
  /** Why the share was deferred (retryable by anyone), if it was. */
  releaseNote?: string | null;
  /** `claimWork([tokenId])` — this round's wage — when it landed. */
  workTxHash?: string | null;
  /** Why the wage was deferred (e.g. no round open), if it was. */
  workNote?: string | null;
}

/** Sign + submit one model change. Returns once the relay has the tx hash. */
export async function setAgentModel(params: {
  wallet: WalletClient;
  account: Address;
  tokenId: bigint;
  modelId: number;
  nonce: bigint;
  chainId: number;
  contractAddress: Address;
  apiBase: string;
}): Promise<RelayOutcome> {
  const typedData = buildSetModelTypedData({
    tokenId: params.tokenId,
    holder: params.account,
    modelId: params.modelId,
    nonce: params.nonce,
    chainId: params.chainId,
    contractAddress: params.contractAddress,
  });

  const signature = await params.wallet.signTypedData({
    account: params.account,
    domain: typedData.domain,
    types: typedData.types,
    primaryType: typedData.primaryType,
    message: typedData.message,
  });

  return submitSetModel({
    apiBase: params.apiBase,
    tokenId: params.tokenId,
    holder: params.account,
    modelId: params.modelId,
    nonce: params.nonce,
    deadline: typedData.message.deadline,
    signature,
  });
}

/**
 * The full flow a holder experiences on the site:
 *   connect -> sign one message -> agent wakes.
 */
export async function activateAgent(params: {
  wallet: WalletClient;
  account: Address;
  tokenId: bigint;
  chainId: number;
  contractAddress: Address;
  apiBase: string;
}): Promise<RelayOutcome> {
  const typedData = buildActivationTypedData({
    tokenId: params.tokenId,
    holder: params.account,
    chainId: params.chainId,
    contractAddress: params.contractAddress,
  });

  const signature = await signActivation(params.wallet, params.account, typedData);

  return submitActivation({
    apiBase: params.apiBase,
    tokenId: params.tokenId,
    holder: params.account,
    deadline: typedData.message.deadline,
    signature,
  });
}